import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePortaledListBoxMenu } from 'components/type-curve-workspace/use-portaled-list-box-menu';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { tcSelectedDevAreaAtom } from 'pages/type-curves/type-curve-filters/atoms';
import { useUnitShapeEdit } from 'components/geometry-editing';
import {
  AUTO_RESOLVE_DEFAULT,
  autoResolveOverlapsAtom,
  boundaryUserEditedAtom,
  builderUnitNameAtom,
  savedDraftAtom,
  surveyRowsAtom,
} from './atoms';
import { useUnitBoundaryGeometry } from './use-unit-boundary-geometry';
import { UnitBuilderContext, type UnitBuilderContextValue } from './context';
import { SurfaceContext } from 'pages/type-curves/inventory-surface/surface';
import { UnitBuilderHeader } from './unit-builder-header';
import { UnitBuilderMapPane } from './unit-builder-map-pane';
import { UnitBuilderGunBarrelPane } from './unit-builder-gun-barrel-pane';
import { UnitRegenerationBanner } from './unit-regeneration-banner';
import style from './unit-builder-modal.module.scss';

/** Mock default for the map column, and the drag limits either side of it. */
const DEFAULT_MAP_COL_PCT = 36;
const MIN_MAP_COL_PCT = 20;
const MAX_MAP_COL_PCT = 70;

interface UnitBuilderModalProps {
  open: boolean;
  inventoryWorkspaceId: number;
  /** Null while the builder is open on an unsaved draft unit. */
  unitId: number | null;
  unitName: string | null;
  /** The scoped leaf's header detail, which the gun barrel keys on. */
  typeCurveHeaderDetailsId?: number | null;
  /** The workspace has been promoted: composites are frozen. */
  promoted?: boolean;
  onClose: () => void;
  /** Prev/next pager for the header. */
  pager?: ReactNode;
  /**
   * The two panes. Each is a single slot because the mock splits the builder the
   * same way: the left pane is `EditSlotMap` (toolbar, map, survey areas) and
   * the right is the gun barrel in edit mode (controls, chart, edit rail, slots
   * table). Sub-sections belong to those components, not to this shell.
   */
  mapPane?: ReactNode;
  gunBarrelPane?: ReactNode;
}

/**
 * Unit Builder — edit one unit's boundary, azimuth and slot packing.
 *
 * The shell only: header, and the two resizable panes laid out as the mock's
 * `editSlotSplit` does — `minmax(0, var(--unit-builder-map-col, 36%)) 8px
 * minmax(0, 1fr)` over a single full-height row, with a draggable divider
 * between them. Nothing here fetches or mutates.
 *
 * @param {UnitBuilderModalProps} props - Open state, scoped unit and pane slots.
 * @return {JSX.Element} The modal.
 */
export const UnitBuilderModal: FC<UnitBuilderModalProps> = ({
  open,
  inventoryWorkspaceId,
  unitId,
  unitName,
  typeCurveHeaderDetailsId = null,
  promoted = false,
  onClose,
  pager,
  mapPane,
  gunBarrelPane,
}) => {
  const splitRef = useRef<HTMLDivElement>(null);
  const [mapColPct, setMapColPct] = useState(DEFAULT_MAP_COL_PCT);
  const [dragging, setDragging] = useState(false);

  // The unit's footprint. The map layer can only reconstruct a unit as a rotated
  // bounding rectangle (centroid + azimuth + length + width), which overshoots
  // any unit whose real shape isn't that rectangle. A persisted unit has an exact
  // polygon on the server, so a REAL unit ALWAYS edits that true polygon — never
  // the frame rectangle (it holds until the server geometry loads). A CLONE seeds
  // from `selection.ring`, which the clone action copies off the source unit's
  // true polygon, so it is not rectangular either; only a brand-new unit (no
  // server geometry at all) falls back to the frame rectangle.
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  const frameRing = selectedDevArea?.ring ?? null;
  // Read by the REAL id: a clone has no id of its own but carries the source unit
  // it was copied from, and that unit's geometry + stored acreage are already
  // cached (same query key, `staleTime: Infinity`) from having it open — so this
  // costs no extra request. A brand-new unit has no selection, so nothing loads.
  const boundaryUnitId =
    unitId ?? selectedDevArea?.sourceDevelopmentAreaId ?? null;
  const unitBoundary = useUnitBoundaryGeometry(open ? boundaryUnitId : null);
  // The RING is only taken for a real unit. A clone seeds from its own
  // `selection.ring` (which the clone action now fills with the source's true
  // polygon) — never from this async read, which would re-seed the shape editor
  // and wipe a reshape the user had already made.
  const originalRing = unitId !== null ? unitBoundary.ring : frameRing;
  // The base of the boundary composition, and the one value that must NOT drift:
  // a clone's `frameRing` comes off `selection.ring`, which the clone write-back
  // refreshes from the edited boundary, so composing against it would union a
  // shape with itself. This read is keyed on the real unit id and cached, so it
  // stays the unit's true footprint however the boundary is edited.
  const baseRing = unitBoundary.ring;

  // A unit that is not persisted yet — a session clone OR a brand-new unit built
  // from scratch. Both are unsaved drafts (`unitId === null`), so both may be
  // renamed and both need the overlap opt-in; an existing unit gets neither.
  const isDraftUnit = unitId === null;

  // "Auto-resolve overlaps" shows for any unsaved draft (clone or brand-new) and
  // starts unchecked on each one (keyed on the scoped unit's id, which changes
  // per clone).
  const setAutoResolve = useSetAtom(autoResolveOverlapsAtom);
  // A typed name belongs to the unit it was typed for, so it is dropped on the
  // same trigger as the overlap opt-in — otherwise paging to the next draft would
  // inherit the previous one's name.
  const setDraftName = useSetAtom(builderUnitNameAtom);
  // A reshape belongs to the unit it was made on, so the Save provenance flag is
  // dropped on the same trigger as the name and the overlap opt-in.
  const setBoundaryUserEdited = useSetAtom(boundaryUserEditedAtom);
  // The save guard and any unfinished adoption belong to the unit that was being
  // saved, so they clear on the same trigger: moving on lets the next draft save,
  // and abandons a wait whose result nobody is looking at any more.
  const setSavedDraft = useSetAtom(savedDraftAtom);
  const scopedUnitKey = selectedDevArea?.developmentAreaId;
  useEffect(() => {
    setAutoResolve(AUTO_RESOLVE_DEFAULT);
    setDraftName(null);
    setBoundaryUserEdited(false);
    setSavedDraft(false);
  }, [
    open,
    scopedUnitKey,
    setAutoResolve,
    setDraftName,
    setBoundaryUserEdited,
    setSavedDraft,
  ]);
  const seed = useMemo(
    () => (open && originalRing ? [originalRing] : null),
    [open, originalRing]
  );
  // Survey rows ride along in the undo history: the boundary is composed from
  // them, so adding or removing one is a boundary change, and a single undo has
  // to reverse both halves or the row list and the shape disagree.
  const [surveyRows, setSurveyRows] = useAtom(surveyRowsAtom);
  const shapeEdit = useUnitShapeEdit(seed, {
    value: surveyRows,
    restore: setSurveyRows,
  });

  // Empty until the panes that own selection are wired; held here so both read
  // one source rather than each keeping its own copy.
  const context = useMemo<UnitBuilderContextValue>(
    () => ({
      inventoryWorkspaceId,
      unitId,
      unitName,
      typeCurveHeaderDetailsId,
      promoted,
      scopedSlotKeys: new Set<number>(),
      unvaluedSlotKeys: new Set<string>(),
      shapeEdit,
      originalRing,
      baseRing,
      unitAcreage: unitBoundary.acreage,
    }),
    [
      inventoryWorkspaceId,
      promoted,
      unitId,
      unitName,
      typeCurveHeaderDetailsId,
      shapeEdit,
      originalRing,
      baseRing,
      unitBoundary.acreage,
    ]
  );

  const handleDividerMouseDown = useCallback(() => setDragging(true), []);

  useEffect(() => {
    if (!dragging) return;

    const onMove = (event: MouseEvent) => {
      const box = splitRef.current?.getBoundingClientRect();
      if (!box || box.width === 0) return;
      const pct = ((event.clientX - box.left) / box.width) * 100;
      setMapColPct(Math.min(MAX_MAP_COL_PCT, Math.max(MIN_MAP_COL_PCT, pct)));
    };
    const onUp = () => setDragging(false);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  // With the body's overflow pinned (see the SCSS), an in-flow ComboBox menu is
  // clipped by the column's scroller. Move the open one to a fixed host outside
  // the modal instead — the same hook the create-inventory-workspace modal uses.
  const menuPortalContainerRef = usePortaledListBoxMenu<HTMLDivElement>(
    open,
    style.menuPortalHost
  );

  return (
    // Everything inside the builder renders under the 'builder' surface, so the
    // shared Gun Barrel / Slot Table / Map read the builder's OWN state atoms
    // (seeded from the main page on open) rather than the main-page copies.
    <SurfaceContext.Provider value="builder">
      {/* A bottom sheet rather than a Carbon modal, matching the DCA modal
          (`type-curve-dca/tc-dca-modal.tsx`). Owning the container outright is
          what lets the panes take a share of the viewport, and it sidesteps
          Carbon's `.bx--modal-content:has(.bx--list-box--expanded) { overflow:
          visible }`, which released this body's scroll container whenever a
          survey dropdown opened and pushed both columns out through the bottom
          edge.

          The backdrop dims but is deliberately inert: the builder holds unsaved
          boundary and survey edits with no confirm-on-close, so a stray click
          outside must not discard them. Closing is the header's × only. */}
      <div
        className={style.sheet}
        role="dialog"
        aria-modal="true"
        aria-label="Unit Builder"
      >
        <div className={style.sheetBackdrop} />
        <div className={style.sheetContent} ref={menuPortalContainerRef}>
          <UnitBuilderContext.Provider value={context}>
            <UnitBuilderHeader
              unitName={unitName}
              pager={pager}
              unitHref={unitId ? `/development_areas/${unitId}` : undefined}
              showAutoResolve={isDraftUnit}
              nameEditable={isDraftUnit}
              onClose={onClose}
            />

            {/* A rotate re-lays every formation package in the background; this
                is the operator's only sign it is happening from inside the
                builder. Same tally the main page shows. */}
            <UnitRegenerationBanner />

            <div
              ref={splitRef}
              className={style.split}
              style={
                {
                  '--unit-builder-map-col': `${mapColPct}%`,
                } as React.CSSProperties
              }
            >
              <UnitBuilderMapPane>{mapPane}</UnitBuilderMapPane>

              <div
                className={style.divider}
                onMouseDown={handleDividerMouseDown}
                role="separator"
                aria-label="Resize map and gun barrel panes"
                aria-orientation="vertical"
              >
                <span className={style.dividerGrip} aria-hidden>
                  ⠿
                </span>
              </div>

              <UnitBuilderGunBarrelPane>
                {gunBarrelPane}
              </UnitBuilderGunBarrelPane>
            </div>
          </UnitBuilderContext.Provider>
        </div>
      </div>
    </SurfaceContext.Provider>
  );
};
